import React from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../auth/AuthContext';
import { canonicalRequest } from './CanonicalEditorCommon';

function decimalText(cents) {
  return (Number(cents || 0) / 100).toFixed(2);
}

function percentText(basisPoints) {
  return (Number(basisPoints || 0) / 100).toFixed(2).replace(/\.00$/, '');
}

function emptyPolicyForm() {
  return {
    name: '',
    currency: 'USD',
    targetMargin: '40',
    percentageFee: '0',
    fixedFee: '0.00',
    defaultShipping: '0.00',
    minimumPrice: '0.00',
    endingCents: '95',
    active: true,
  };
}

function formFromPolicy(policy) {
  if (!policy) return emptyPolicyForm();
  return {
    name: policy.name || '',
    currency: policy.currency || 'USD',
    targetMargin: percentText(policy.target_margin_bps),
    percentageFee: percentText(policy.percentage_fee_bps),
    fixedFee: decimalText(policy.fixed_fee_cents),
    defaultShipping: decimalText(policy.default_shipping_cents),
    minimumPrice: decimalText(policy.minimum_price_cents),
    endingCents: String(policy.ending_cents ?? 95),
    active: policy.active !== false,
  };
}

function nonNegativeNumber(value, label) {
  const parsed = Number.parseFloat(String(value || '').trim());
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error(`${label} must be a non-negative number.`);
  }
  return parsed;
}

function policyPayload(form, policy) {
  const endingCents = Number.parseInt(form.endingCents, 10);
  if (!Number.isInteger(endingCents) || endingCents < 0 || endingCents > 99) {
    throw new Error('Ending cents must be a whole number from 0 through 99.');
  }
  const payload = {
    name: form.name.trim(),
    currency: form.currency.trim().toUpperCase(),
    target_margin_bps: Math.round(nonNegativeNumber(form.targetMargin, 'Target margin') * 100),
    percentage_fee_bps: Math.round(nonNegativeNumber(form.percentageFee, 'Percentage fee') * 100),
    fixed_fee_cents: Math.round(nonNegativeNumber(form.fixedFee, 'Fixed fee') * 100),
    default_shipping_cents: Math.round(nonNegativeNumber(form.defaultShipping, 'Default shipping') * 100),
    minimum_price_cents: Math.round(nonNegativeNumber(form.minimumPrice, 'Minimum price') * 100),
    ending_cents: endingCents,
    active: form.active,
  };
  if (policy) payload.expected_version = policy.version;
  return payload;
}

const CanonicalPricingPolicyDialog = ({ open, policy, onClose, onSaved }) => {
  const { fetchWithAuth } = useAuth();
  const [form, setForm] = React.useState(emptyPolicyForm);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setForm(formFromPolicy(policy));
    setError('');
  }, [open, policy]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = policyPayload(form, policy);
      const result = await canonicalRequest(fetchWithAuth, policy ? `/pricing-policies/${policy.id}` : '/pricing-policies', {
        method: policy ? 'PATCH' : 'POST',
        body: JSON.stringify(payload),
      });
      onSaved?.(result.item);
    } catch (saveError) {
      setError(saveError.status === 409
        ? 'This policy changed since it was loaded. Close and reopen it before saving.'
        : saveError.message || 'Unable to save pricing policy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="pricing-policy-title">
      <DialogTitle id="pricing-policy-title">{policy ? 'Edit pricing policy' : 'New pricing policy'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Policies calculate a local suggestion only. They never update a provider until a listing is saved and a separate publication workflow is applied.
          </Typography>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField label="Policy name" required value={form.name} onChange={(event) => setField('name', event.target.value)} autoFocus />
          <TextField label="Currency" required value={form.currency} onChange={(event) => setField('currency', event.target.value.toUpperCase())} inputProps={{ maxLength: 3 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth label="Target margin (%)" value={form.targetMargin} onChange={(event) => setField('targetMargin', event.target.value)} inputMode="decimal" />
            <TextField fullWidth label="Percentage fees (%)" value={form.percentageFee} onChange={(event) => setField('percentageFee', event.target.value)} inputMode="decimal" />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth label={`Fixed fee (${form.currency})`} value={form.fixedFee} onChange={(event) => setField('fixedFee', event.target.value)} inputMode="decimal" />
            <TextField fullWidth label={`Default shipping (${form.currency})`} value={form.defaultShipping} onChange={(event) => setField('defaultShipping', event.target.value)} inputMode="decimal" />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth label={`Minimum price (${form.currency})`} value={form.minimumPrice} onChange={(event) => setField('minimumPrice', event.target.value)} inputMode="decimal" />
            <TextField fullWidth label="Price ending (cents)" value={form.endingCents} onChange={(event) => setField('endingCents', event.target.value)} inputMode="numeric" helperText="For example, 95 rounds upward to prices ending in .95." />
          </Stack>
          <FormControlLabel control={<Checkbox checked={form.active} onChange={(event) => setField('active', event.target.checked)} />} label="Available for new calculations" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save policy'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CanonicalPricingPolicyDialog;
